import React, { useEffect, useState } from 'react';
import { getApi } from '../../services/api';

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getApi('profile');
        if (res.data?.status === true) {
          setUserData(res.data?.USER || res.data?.data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const copyCode = () => {
    const code = userData?.my_code || '';
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const truncate = (str) => str ? `${str.slice(0, 6)}••••••${str.slice(-6)}` : '—';

  return (
    <div className="page-wrapper">
      {/* Header */}
      <div className="page-header mb-4 d-flex justify-content-between align-items-start">
        <div>
          <div className="section-label">Account</div>
          <h2 className="page-title">My Profile</h2>
          <p className="page-subtitle">Manage your personal information and network stats</p>
        </div>
        <span className="s-pill active d-flex align-items-center gap-2">
          <span className="dot-live"></span> Active
        </span>
      </div>

      <div className="row g-4">
        {/* Left Column — Avatar + Code */}
        <div className="col-lg-4">
          <div className="p-card text-center" style={{ paddingBottom: '2rem' }}>
            {loading ? (
              <>
                <div className="skeleton mx-auto mb-3" style={{ width: 110, height: 110, borderRadius: '50%' }}></div>
                <div className="skeleton mx-auto mb-2" style={{ width: 140, height: 20 }}></div>
                <div className="skeleton mx-auto" style={{ width: 90, height: 16 }}></div>
              </>
            ) : (
              <>
                {/* Avatar */}
                <div className="position-relative d-inline-block mb-3">
                  <div style={{
                    width: 110, height: 110, borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(0,255,149,0.3), rgba(0,212,255,0.2))',
                    padding: 3, margin: '0 auto'
                  }}>
                    <img
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(userData?.my_code?.slice(0, 8) || 'User')}&background=071B12&color=00FF95&size=150&bold=true`}
                      alt="Avatar"
                      style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                    />
                  </div>
                  <span className="dot-live" style={{ position: 'absolute', bottom: 8, right: 8 }}></span>
                </div>

                {/* Code */}
                <div className="mb-1">
                  <span className="u-code" style={{ fontSize: '0.88rem' }}>
                    {truncate(userData?.my_code)}
                  </span>
                </div>
                <span className="s-pill active mb-3">{userData?.rank || 'Member'}</span>

                {/* Copy Referral */}
                <div className="mt-3 p-3" style={{ background: 'rgba(0,255,149,0.05)', borderRadius: 14, border: '1px solid rgba(0,255,149,0.2)' }}>
                  <p className="p-label mb-1">Your Referral Code</p>
                  <div className="d-flex align-items-center gap-2 justify-content-center">
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.9rem', color: 'var(--page-accent)', fontWeight: 700 }}>

                      {userData.my_code?.slice(0, 6)}••••{userData.my_code?.slice(-4) || '-'}
                    </span>
                    <button className={`copy-btn ${copied ? 'copied' : ''}`} onClick={copyCode}>
                      <i className={`bi ${copied ? 'bi-check2' : 'bi-copy'}`}></i>{' '}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Quick Stats */}
          <div className="p-card mt-4">
            <p className="section-label">Account Stats</p>
            {loading ? (
              [1, 2, 3, 4].map(i => <div key={i} className="skeleton mb-2" style={{ height: 36 }}></div>)
            ) : (
              <>
                {[
                  { label: 'Today Income', value: `$${userData?.today_income || 0}`, color: 'var(--page-accent)' },
                  { label: 'Total Income', value: `$${userData?.total_income || 0}`, color: 'var(--page-blue)' },
                  { label: 'Available Balance', value: `$${userData?.available_amount || 0}`, color: 'var(--page-warning)' },
                  { label: 'My Referrals', value: userData?.my_referral || 0, color: 'var(--page-purple)' },
                ].map((s, i) => (
                  <div key={i} className="info-row">
                    <span className="i-label">{s.label}</span>
                    <span className="i-value" style={{ color: s.color }}>{s.value}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Right Column — Network + Details */}
        <div className="col-lg-8">
          {/* Network Overview */}
          <div className="p-card mb-4">
            <p className="section-label">Network Overview</p>
            <div className="row g-3">
              {[
                { label: 'Left Users', value: userData?.left_users || 0, icon: 'bi-people', color: 'green' },
                { label: 'Right Users', value: userData?.right_users || 0, icon: 'bi-people', color: 'blue' },
                { label: 'Left Business', value: `$${userData?.left_business || 0}`, icon: 'bi-graph-up', color: 'yellow' },
                { label: 'Right Business', value: `$${userData?.right_business || 0}`, icon: 'bi-graph-up', color: 'purple' },
              ].map((m, i) => (
                <div key={i} className="col-md-6">
                  {loading ? (
                    <div className="skeleton" style={{ height: 90, borderRadius: 18 }}></div>
                  ) : (
                    <div className={`metric-card ${m.color}`}>
                      <div className={`metric-icon ${m.color}`}>
                        <i className={`bi ${m.icon}`}></i>
                      </div>
                      <div>
                        <div className="metric-label">{m.label}</div>
                        <div className="metric-value">{m.value}</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Tree Progress */}
          {!loading && (
            <div className="p-card mb-4">
              <p className="section-label">Binary Tree Balance</p>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="p-progress-wrap">
                    <div className="p-progress-header">
                      <span className="p-progress-label">Left Side</span>
                      <span className="p-progress-value">{userData?.left_users || 0} users</span>
                    </div>
                    <div className="p-progress">
                      <div className="p-progress-bar" style={{
                        width: `${(userData?.left_users || 0) > 0 ? Math.min(((userData?.left_users || 0) / ((userData?.left_users || 0) + (userData?.right_users || 1))) * 100, 100) : 0}%`
                      }}></div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-progress-wrap">
                    <div className="p-progress-header">
                      <span className="p-progress-label">Right Side</span>
                      <span className="p-progress-value">{userData?.right_users || 0} users</span>
                    </div>
                    <div className="p-progress">
                      <div className="p-progress-bar" style={{
                        width: `${(userData?.right_users || 0) > 0 ? Math.min(((userData?.right_users || 0) / ((userData?.left_users || 1) + (userData?.right_users || 0))) * 100, 100) : 0}%`,
                        background: 'linear-gradient(90deg, var(--page-blue), var(--page-purple))'
                      }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Account Details */}
          <div className="p-card">
            <p className="section-label">Account Information</p>
            <div className="row g-3">
              {[
                { label: 'Account ID', value: userData?.my_code ? truncate(userData.my_code) : '—', mono: true },
                { label: 'Current Rank', value: userData?.rank || 'Member' },
                { label: 'Today Income', value: `$${userData?.today_income || 0}` },
                { label: 'Total Income', value: `$${userData?.total_income || 0}` },
              ].map((d, i) => (
                <div key={i} className="col-md-6">
                  <div style={{ padding: '0.9rem 1rem', background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="p-label mb-1">{d.label}</p>
                    <p className={`mb-0 fw-700`} style={{ fontFamily: d.mono ? 'JetBrains Mono, monospace' : 'inherit', fontSize: '0.95rem', color: 'var(--text-primary)' }}>{d.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;